// Vendors
import { StripePaymentElementChangeEvent } from '@stripe/stripe-js';
// Resolvers
import { checkPaymentResolver } from '../resolvers/payment-landing-form.resolvers';
// Types
import {
  PaymentLandindFormButtonConfigurationHandleType,
  PaymentLandingFormBuilderHandlersType,
  PaymentLandingFormCompleteHandlerType,
  PaymentLandingFormMethodsHandlersType,
  SubmitPaymentHandlerType,
  UpdateFormButtonLoadingConfigHandlerType,
} from './types/payment-landing-form.handlers.type';
// Utils
import {
  checkValidOperation,
  confirmPayment,
  createPaymentMethod,
  submitElements,
} from './utils/payment-landing-form.handlers.utils';

const updateFormButtonsLoadingConfigHandler = ({
  loading,
  setButtonGhostConfiguration,
  setMainButtonConfiguration,
}: UpdateFormButtonLoadingConfigHandlerType): void => {
  setMainButtonConfiguration(prev => ({
    ...prev,
    loading,
  }));
  setButtonGhostConfiguration(prev => ({
    ...prev,
    disabled: loading,
  }));
};

// eslint-disable-next-line complexity
const submitPaymentHandler = async ({
  cardCountry,
  e,
  elements,
  setButtonGhostConfiguration,
  setError,
  setMainButtonConfiguration,
  stripe,
}: SubmitPaymentHandlerType): Promise<void> => {
  e.preventDefault();

  if (!stripe || !elements) {
    return;
  }

  updateFormButtonsLoadingConfigHandler({
    loading: true,
    setButtonGhostConfiguration,
    setMainButtonConfiguration,
  });

  const submitResultError = await submitElements({ elements });
  if (submitResultError) {
    setError(true);
    return;
  }

  const paymentMethod = await createPaymentMethod({
    cardCountry,
    elements,
    stripe,
  });
  if (!paymentMethod?.id) {
    setError(true);
    return;
  }

  if (paymentMethod.card) {
    const [isValidOperation] = await checkPaymentResolver({
      brand: paymentMethod.card.brand,
      country: cardCountry,
      funding: paymentMethod.card.funding,
      monthExp: paymentMethod.card.exp_month.toString(),
      yearExp: paymentMethod.card.exp_year.toString(),
    });

    if (!checkValidOperation(isValidOperation?.status)) {
      setError(true);
      return;
    }
  } else {
    setError(true);
    return;
  }

  const paymentConfirmationError = await confirmPayment({
    cardCountry,
    elements,
    stripe,
    paymentMethodId: paymentMethod.id,
  });

  if (paymentConfirmationError) {
    setError(true);
  }

  updateFormButtonsLoadingConfigHandler({
    loading: !paymentConfirmationError,
    setButtonGhostConfiguration,
    setMainButtonConfiguration,
  });
};

const paymentLandingFormButtonConfigurationHandler = ({
  completeForm,
  handleTrackPayButtonClick,
  setMainButtonConfiguration,
  ...props
}: PaymentLandindFormButtonConfigurationHandleType): void => {
  setMainButtonConfiguration(prev => ({
    ...prev,
    disabled: !completeForm,
    onClick: e => {
      e.preventDefault();
      e.stopPropagation();
      handleTrackPayButtonClick();
      submitPaymentHandler({
        e,
        ...props,
        setMainButtonConfiguration,
      });
    },
  }));
};

const paymentFormCompleteHandler = ({
  event,
  setCompleteForm,
}: PaymentLandingFormCompleteHandlerType): void => {
  setCompleteForm(event.complete);
};

const PaymentLandingFormHandlers = (
  props: PaymentLandingFormBuilderHandlersType
): PaymentLandingFormMethodsHandlersType => ({
  handlePaymentLandingFormButtonConfiguration: () =>
    paymentLandingFormButtonConfigurationHandler(props),
  handlePaymentFormComplete: (event: StripePaymentElementChangeEvent) =>
    paymentFormCompleteHandler({ event, ...props }),
});

export { PaymentLandingFormHandlers, submitPaymentHandler };
